import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';

const WhatsAppButton = ({ 
  phoneNumber = '5511939614816', // Substitua pelo seu número
  message = 'Olá! Vim pelo site MeuMei e gostaria de saber mais sobre a plataforma.',
  position = 'bottom-right' // 'bottom-right' ou 'bottom-left'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Mostrar tooltip após 5 segundos se o usuário não interagiu
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasInteracted) {
        setShowTooltip(true);
        // Esconder tooltip após 5 segundos
        setTimeout(() => setShowTooltip(false), 5000);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [hasInteracted]);

  // Função para abrir WhatsApp
  const openWhatsApp = (customMessage) => {
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(customMessage || message)}`;
    window.open(url, '_blank');
    
    // Track evento (se tiver analytics)
    // eslint-disable-next-line no-undef
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'whatsapp_click', {
        'event_category': 'Contact',
        'event_label': 'WhatsApp Button',
      });
    }
  };

  // Mensagens rápidas
  const quickMessages = [
    { text: '💰 Quero saber sobre os planos', message: 'Olá! Gostaria de saber mais sobre os planos do MeuMei.' },
    { text: '🆓 Como funciona o período grátis?', message: 'Olá! Como funciona o período de 7 dias grátis?' },
    { text: '📝 Ajuda para abrir meu MEI', message: 'Olá! Preciso de ajuda para abrir meu MEI.' },
    { text: '❓ Tenho uma dúvida', message: 'Olá! Tenho uma dúvida sobre o MeuMei.' },
  ];

  const positionClasses = position === 'bottom-left' 
    ? 'left-4 sm:left-6' 
    : 'right-4 sm:right-6';

  return (
    <>
      {/* Botão Principal */}
      <div className={`fixed bottom-20 sm:bottom-6 ${positionClasses} z-50`}>
        <AnimatePresence>
          {/* Tooltip */}
          {showTooltip && !isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              className={`absolute bottom-16 ${position === 'bottom-left' ? 'left-0' : 'right-0'} bg-white rounded-xl shadow-lg p-4 w-64`}
            >
              <button 
                onClick={() => setShowTooltip(false)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
              <p className="text-gray-800 text-sm font-medium mb-2">
                👋 Precisa de ajuda?
              </p>
              <p className="text-gray-600 text-xs">
                Fale conosco pelo WhatsApp! Respondemos em minutos.
              </p>
              {/* Seta */}
              <div className={`absolute -bottom-2 ${position === 'bottom-left' ? 'left-6' : 'right-6'} w-4 h-4 bg-white transform rotate-45 shadow-lg`} />
            </motion.div>
          )}

          {/* Menu de Opções */}
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className={`absolute bottom-16 ${position === 'bottom-left' ? 'left-0' : 'right-0'} bg-white rounded-2xl shadow-2xl overflow-hidden w-72`}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold">MeuMei</p>
                    <p className="text-xs opacity-90">Online agora</p>
                  </div>
                </div>
              </div>

              {/* Mensagem de boas-vindas */}
              <div className="p-4 bg-gray-50">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <p className="text-gray-700 text-sm">
                    Olá! 👋 Como posso te ajudar hoje?
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Agora</p>
                </div>
              </div>

              {/* Opções rápidas */}
              <div className="p-3 space-y-2">
                <p className="text-xs text-gray-500 px-1 mb-2">Escolha uma opção:</p>
                {quickMessages.map((item, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => openWhatsApp(item.message)}
                    className="w-full text-left p-3 bg-gray-50 hover:bg-green-50 rounded-xl text-sm text-gray-700 hover:text-green-700 transition-colors"
                  >
                    {item.text}
                  </motion.button>
                ))}
              </div>

              {/* Input customizado */}
              <div className="p-3 border-t">
                <button
                  onClick={() => openWhatsApp(message)}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Iniciar Conversa
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Botão do WhatsApp */}
        <motion.button
          onClick={() => {
            setIsOpen(!isOpen);
            setHasInteracted(true);
            setShowTooltip(false);
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="relative w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg flex items-center justify-center text-white transition-colors"
          style={{
            boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4)',
          }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6" />
              </motion.div>
            ) : (
              <motion.div
                key="whatsapp"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Indicador de online */}
          {!isOpen && (
            <span className="absolute top-0 right-0 w-3 h-3 bg-green-300 rounded-full border-2 border-white">
              <span className="absolute inset-0 bg-green-400 rounded-full animate-ping" />
            </span>
          )}
        </motion.button>
      </div>
    </>
  );
};

export default WhatsAppButton;
